// Achievement system for KiddiFit™ - designed for 6-8 year olds

// Level calculation formula
export const calculateLevel = (totalPoints) => {
  // Formula: level = Math.floor(totalPoints / 100)
  // This means: Level 0 at 0-99 points, Level 1 at 100-199 points, etc.
  return Math.floor(totalPoints / 100);
};

// Get points required for next level
export const getPointsForNextLevel = (currentLevel) => {
  return (currentLevel + 1) * 100;
};

// Get current level info
export const getCurrentLevelInfo = (totalPoints) => {
  const level = calculateLevel(totalPoints);
  const pointsForNextLevel = getPointsForNextLevel(level);
  const pointsInCurrentLevel = totalPoints % 100;

  return {
    level,
    name: `Level ${level}`,
    emoji: '🛡️',
    pointsRequired: level * 100,
    pointsForNextLevel,
    pointsInCurrentLevel,
    progress: (pointsInCurrentLevel / 100) * 100, // 0-100%
  };
};

// Streak milestones
export const STREAK_MILESTONES = {
  3: { name: '3-Day Superstar!', emoji: '⭐', message: "Wow! You're on fire!" },
  7: { name: 'Week Warrior!', emoji: '🏆', message: 'Amazing! A whole week!' },
  14: {
    name: 'Two Week Champion!',
    emoji: '🎉',
    message: "You're unstoppable!",
  },
  30: {
    name: 'Monthly Master!',
    emoji: '🌟',
    message: 'Incredible! A whole month!',
  },
};

// Points milestones
export const POINTS_MILESTONES = {
  100: {
    name: '100 Points!',
    emoji: '🎊',
    message: "Amazing! You're a superstar!",
  },
  250: {
    name: 'Quarter Master!',
    emoji: '🎯',
    message: 'Fantastic! Keep going!',
  },
  500: {
    name: 'Halfway Hero!',
    emoji: '🚀',
    message: "Wow! You're getting so strong!",
  },
  1000: {
    name: 'Thousandaire!',
    emoji: '👑',
    message: "Incredible! You're a champion!",
  },
};

// Check for new levels earned
export const checkLevels = (totalPoints, previousTotalPoints = 0) => {
  const previousLevel = calculateLevel(previousTotalPoints);
  const currentLevel = calculateLevel(totalPoints);

  if (currentLevel > previousLevel) {
    // Calculate bonus points for leveling up
    const bonusPoints = currentLevel * 10; // 10 points per level as bonus

    return {
      newLevel: currentLevel,
      levelUp: true,
      bonusPoints,
      message: `🎉 LEVEL UP! You reached Level ${currentLevel}! 🎉\n+${bonusPoints} bonus points!`,
    };
  }

  return {
    levelUp: false,
    bonusPoints: 0,
  };
};

// Check for new streak milestones
export const checkStreakMilestones = (streakDays, achievedMilestones = []) => {
  const newMilestones = [];

  Object.entries(STREAK_MILESTONES).forEach(([days, milestone]) => {
    if (
      streakDays >= parseInt(days) &&
      !achievedMilestones.includes(`streak_${days}`)
    ) {
      newMilestones.push({
        type: 'streak',
        id: `streak_${days}`,
        ...milestone,
      });
    }
  });

  return newMilestones;
};

// Check for new points milestones
export const checkPointsMilestones = (totalPoints, achievedMilestones = []) => {
  const newMilestones = [];

  Object.entries(POINTS_MILESTONES).forEach(([points, milestone]) => {
    if (
      totalPoints >= parseInt(points) &&
      !achievedMilestones.includes(`points_${points}`)
    ) {
      newMilestones.push({
        type: 'points',
        id: `points_${points}`,
        ...milestone,
      });
    }
  });

  return newMilestones;
};

// Get all achievements for a player
export const getAllAchievements = (playerData, previousTotalScore = 0) => {
  const { totalScore = 0, streakDays = 0, achievements = {} } = playerData;
  const achievedMilestones = achievements.milestones || [];

  const levelResult = checkLevels(totalScore, previousTotalScore);
  const newStreakMilestones = checkStreakMilestones(
    streakDays,
    achievedMilestones
  );
  const newPointsMilestones = checkPointsMilestones(
    totalScore,
    achievedMilestones
  );

  return {
    levelResult,
    newStreakMilestones,
    newPointsMilestones,
    allNew: [
      ...(levelResult.levelUp ? [levelResult] : []),
      ...newStreakMilestones,
      ...newPointsMilestones,
    ],
  };
};

// Get current level
export const getCurrentLevel = (totalPoints) => {
  return getCurrentLevelInfo(totalPoints);
};

// Get next level to achieve
export const getNextLevel = (totalPoints) => {
  const currentLevelInfo = getCurrentLevelInfo(totalPoints);
  const nextLevel = currentLevelInfo.level + 1;

  return {
    level: nextLevel,
    name: `Level ${nextLevel}`,
    emoji: '🛡️',
    pointsRequired: currentLevelInfo.pointsForNextLevel,
  };
};

// Generate celebration message
export const generateCelebrationMessage = (achievements, playerName) => {
  if (achievements.allNew.length === 0) return null;

  const messages = [];

  if (achievements.levelResult && achievements.levelResult.levelUp) {
    messages.push(achievements.levelResult.message);
  }

  achievements.newStreakMilestones.forEach((milestone) => {
    messages.push(
      `${milestone.emoji} ${milestone.name} ${milestone.emoji}\n${milestone.message}`
    );
  });

  achievements.newPointsMilestones.forEach((milestone) => {
    messages.push(
      `${milestone.emoji} ${milestone.name} ${milestone.emoji}\n${milestone.message}`
    );
  });

  return messages.join('\n\n');
};

// Get family leaderboard data
export const getFamilyLeaderboard = (players) => {
  const sortedPlayers = players
    .map((player) => ({
      id: player.id,
      name: player.playerName,
      avatar: player.avatar,
      points: player.totalScore || 0,
      streak: player.streakDays || 0,
      level: getCurrentLevel(player.totalScore || 0),
    }))
    .sort((a, b) => b.points - a.points);

  // Handle ties by giving same rank to players with same points
  let currentRank = 1;
  let currentPoints = null;

  return sortedPlayers.map((player, index) => {
    if (currentPoints !== null && player.points < currentPoints) {
      currentRank = index + 1;
    }
    currentPoints = player.points;

    return {
      ...player,
      rank: currentRank,
    };
  });
};
